import React from 'react';
import { IpaType } from '../../phonemes/ipa';
import { lexicalSets } from '../../rules/findLexicalSets';
import { meSpeakOptions } from '../../speak/meSpeak';
import { speakPhonemes } from '../../speak/speakPhonemes';
import { wordsToIpa } from '../../transcribe/wordsToIpa';
import { FormantsChart } from '../chart/FormantsChart';
import { segmentPhonemes } from '../../phonemes/segmentPhonemes';
import { ipaToSynthSymbols } from '../../speak/exact';

interface LexicalSetProps {
  readonly one?: { readonly setName: string; readonly phoneme: string; }
  readonly phonemes?: IpaType[];
  readonly options?: meSpeakOptions;
  /** speak the sound when you left or right click on a symbol */
  readonly sayOnClick?: 'left' | 'right';
  readonly clicked?: (ipa: IpaType) => void;
}

/**
 * if (props.one), show info about a single lexical set
 * else if (props.phonemes), show info about a whole list of phonemes
 */
export function LexicalSetArea(props: LexicalSetProps) {
  if (props.phonemes) {
    return AllLexicalSets(props.phonemes);
  }
  if (!props.one) {
    return <></>;
  }
  const { setName, phoneme } = props.one;
  const i = lexicalSets.findIndex(set => set.name === setName);
  if (i === -1) {
    return <></>;
  }
  const set = lexicalSets[i];
  const enUS = getIpa(setName, 'en_US');
  const enUK = getIpa(setName, 'en_UK');
  const current = enUS.replace(set.us, phoneme);
  const all = [set.us, set.uk, phoneme];
  const toColor = whichToColor(set.us).concat(whichToColor(set.uk)).concat(whichToColor(phoneme));

  function speakAll() {
    speakPhonemes(enUS + ' . ' + enUK + ' . ' + current, props.options);
  }
  function speakCurrent() {
    speakPhonemes(current, props.options);
  }

  function speakIpa(ipa: IpaType) {
    speakPhonemes(ipa, props.options);
  }
  function onLeft(ipa: IpaType) {
    if (props.clicked) { props.clicked(ipa); }
    if (props.sayOnClick === 'left') { speakIpa(ipa); }
  }
  const onRight = props.sayOnClick === 'right' ? speakIpa : undefined;

  function whatColor(ipa: IpaType): string {
    return toColor.includes(ipa) ? 'rgb(204, 0, 0)' : 'black';
  }

  return (<>
    <b>Lexical set info</b>
    <br />
    {set.name} - US: {set.us}, UK: {set.uk}, current: {phoneme}
    <br />
    <button onClick={() => speakAll()}>speak all</button>
    &nbsp;&nbsp;&nbsp;
    <button onClick={() => speakCurrent()}>speak current</button>
    <br />
    <FormantsChart width={350} height={250} whatColor={whatColor} diphthongs={all}
      clicked={onLeft} rightClicked={onRight} />
  </>);
}

/** show phonemes from every lexical set */
function AllLexicalSets(phonemes: IpaType[]) {
  const toColor = phonemes.flatMap(p => whichToColor(p));
  function whatColor(ipa: IpaType): string {
    return toColor.includes(ipa) ? 'rgb(204, 0, 0)' : 'black';
  }

  return (<>
    <b>Phonemes from all lexical sets</b>
    <br />
    <FormantsChart width={350} height={250} whatColor={whatColor} diphthongs={phonemes} />
  </>);
}

function getIpa(str: string, dict: 'en_US' | 'en_UK'): string {
  if (str === '') {
    return '';
  }
  const [ipa] = wordsToIpa(str, dict);
  return ipa;
}

/** figure out which symbols we should color */
function whichToColor(ipa: IpaType): IpaType[] {
  // take /ʌ̈ː/, /ɔe̯/, or /ɑɹ/ and figure out which supported phonemes to hilite
  const segments = segmentPhonemes(ipa);
  const phonemes = segments.phonemes.map(p => {
    const ph = p.phoneme;
    // map the rhotics to a close vowel
    if (ph === 'ɚ') return 'ə';
    if (ph === 'ɝ') return 'ɜ';
    // only keep the portion of the phoneme that's directly supported
    const len = ph.length;
    if (len === 1 || ipaToSynthSymbols[ph]) return ph;
    for (let i = len - 1; i > 1; i--) {
      const sub = ph.substring(0, i);
      if (ipaToSynthSymbols[sub]) return sub;
    }
    // larger subset not supported, so just use first character
    return ph[0];
  });

  // just one symbol
  if (phonemes.length === 1) {
    return phonemes;
  }
  // individual symbols + diphthong
  return phonemes.concat(ipa);
}
